import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ReactComponent as CelebrateIcon } from '../../assets/celebrate.svg';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useTheme } from '../../features/theme/context/ThemeContext';

// Function to get proxied image URL for expired S3 URLs
const getProxiedImageUrl = (s3Url) => {
  if (!s3Url) return null;
  
  // If it's already not an S3 URL, just return it as is
  if (!s3Url.includes('amazonaws.com')) {
    return s3Url;
  }
  
  // Extract file key from the S3 URL (everything after the bucket name)
  const urlParts = s3Url.split('/');
  const fileKey = urlParts.slice(3).join('/');
  
  // Return URL that will be handled by our backend proxy endpoint
  return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
};

const InfoModal = ({ modals, onClose, onComplete }) => {
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const { darkMode } = useTheme();

  const currentModal = modals[currentModalIndex];
  const currentSlide = currentModal?.slides[currentSlideIndex];
  const totalSlides = currentModal?.slides?.length || 0;
  const isLastSlide = currentSlideIndex === totalSlides - 1;
  const isLastModal = currentModalIndex === modals.length - 1;

  // Helper function to get the appropriate image URL based on theme
  const getThemeImageUrl = (slide) => {
    if (!slide) return null;
    
    // Use theme-specific URLs (both required)
    return darkMode ? slide.darkImageUrl : slide.lightImageUrl;
  };

  // Helper function to process image URLs (especially for ibb.co)
  const processImageUrl = (url) => {
    if (!url) return url;
    
    // Handle ibb.co URLs - convert page URLs to direct image URLs
    if (url.includes('ibb.co/') && !url.includes('i.ibb.co/')) {
      // Extract the image ID from the URL
      const match = url.match(/ibb\.co\/([A-Za-z0-9]+)/);
      if (match) {
        const imageId = match[1];
        // Try common image extensions
        return `https://i.ibb.co/${imageId}/image.jpg`;
      }
    }
    
    return url;
  };

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      if (onComplete) {
        onComplete(currentModalIndex, currentSlideIndex, false);
      }
      onClose();
    }, 300);
  }, [onComplete, onClose, currentModalIndex, currentSlideIndex]);

  // Auto-animate in when modal opens
  useEffect(() => {
    if (currentModal) {
      setIsAnimating(true);
    }
  }, [currentModal]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  // Reset image loading when theme changes to load appropriate variant
  useEffect(() => {
    setImageLoading(true);
    setImageError(false);
  }, [darkMode, currentSlideIndex, currentModalIndex]);

  const handleNext = () => {
    if (isLastSlide) {
      if (isLastModal) {
        // Completed all modals
        if (onComplete) {
          onComplete(currentModalIndex, currentSlideIndex, true);
        }
        handleClose();
      } else {
        // Move to next modal
        setCurrentModalIndex(currentModalIndex + 1);
        setCurrentSlideIndex(0);
        if (onComplete) {
          onComplete(currentModalIndex, currentSlideIndex, true);
        }
      }
    } else {
      // Move to next slide
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    } else if (currentModalIndex > 0) {
      // Go to previous modal's last slide
      const prevModal = modals[currentModalIndex - 1];
      setCurrentModalIndex(currentModalIndex - 1);
      setCurrentSlideIndex(prevModal.slides.length - 1);
    }
  };

  const goToSlide = (slideIndex) => {
    setImageLoading(true); // Reset loading state when changing slides
    setCurrentSlideIndex(slideIndex);
  };

  // Reset image loading when modal or slide changes
  useEffect(() => {
    setImageLoading(true);
  }, [currentModalIndex, currentSlideIndex]);

  const canGoBack = currentModalIndex > 0 || currentSlideIndex > 0;

  if (!currentModal || !currentSlide) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal Container */}
      <div 
        className={`relative bg-white dark:bg-[#111111] rounded-3xl shadow border border-[#e9e9e9] dark:border-[#181818] max-w-4xl w-full max-h-[90vh] overflow-hidden transition-all duration-300 ${
          isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        {/* Header */}
        <div className="absolute top-0 right-0 z-10 p-1">
          <button
            onClick={handleClose}
            className="p-2 dark:bg-[#111111] bg-white text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors rounded-bl-xl"
            title="Close (Esc)"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content Container */}
        <div className="flex h-full min-h-[500px] max-h-[600px]">
          {/* Left Side - Content */}
          <div className="flex-1 p-8 flex flex-col justify-center">
            <div className="max-w-lg">
                              {/* Badge - New Feature */}
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-black/10 dark:bg-white/10 text-black dark:text-white mb-6">
                  <CelebrateIcon className={`w-3 h-3 mr-2 ${darkMode ? 'text-white' : 'text-black'}`} />
                  NEW
                </div>

              {/* Title */}
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
                {currentSlide.title}
              </h2>

              {/* Description */}
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
                {currentSlide.description}
              </p>

              {/* Slide Indicators */}
              {totalSlides > 1 && (
                <div className="flex items-center space-x-2 mb-6">
                  {currentModal.slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentSlideIndex 
                          ? 'bg-black dark:bg-white' 
                          : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className={`flex items-center ${canGoBack ? 'justify-between' : 'justify-start'}`}>
                {canGoBack && (
                  <button
                    onClick={handlePrevious}
                    className="flex items-center text-sm font-medium transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
                  >
                    <ChevronLeftIcon className="w-4 h-4 mr-1" />
                    Back
                  </button>
                )}

                <button
                  onClick={handleNext}
                  className="flex items-center px-6 py-3 bg-black dark:bg-white hover:bg-[#282828] dark:hover:bg-[#bcbcbc] text-white dark:text-black rounded-xl text-sm font-medium transition-colors"
                >
                  {isLastSlide && isLastModal ? 'Get Started' : 'Next'}
                  {!(isLastSlide && isLastModal) && (
                    <ChevronRightIcon className="w-4 h-4 ml-1" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Side - Image */}
          <div className="flex-1 p-4 pl-0 min-h-[500px]">
            <div className="w-full h-full min-h-[460px] rounded-xl overflow-hidden relative">
              {/* Loading Skeleton - Always show when loading */}
              {imageLoading && (
                <div className="absolute inset-0">
                  <Skeleton 
                    height="100%" 
                    width="100%" 
                    borderRadius="0.75rem"
                    baseColor={darkMode ? "#2a2a2a" : "#f3f4f6"}
                    highlightColor={darkMode ? "#3a3a3a" : "#ffffff"}
                    style={{ display: 'block' }}
                  />
                </div>
              )}
              
              {/* Actual Image */}
              <img
                src={imageError ? getProxiedImageUrl(processImageUrl(getThemeImageUrl(currentSlide))) : processImageUrl(getThemeImageUrl(currentSlide))}
                alt={currentSlide.title}
                className={`w-full h-full object-cover rounded-xl transition-opacity duration-300 ${
                  imageLoading ? 'opacity-0' : 'opacity-100'
                }`}
                onLoad={(e) => {
                  console.log('InfoModal - Image loaded successfully:', e.target.src);
                  setImageLoading(false);
                }}
                onError={(e) => {
                  const themeImageUrl = getThemeImageUrl(currentSlide);
                  console.error('InfoModal - Failed to load image. Theme URL:', themeImageUrl, 'Processed URL:', processImageUrl(themeImageUrl));
                  setImageLoading(false);
                  
                  if (!imageError && themeImageUrl && themeImageUrl.includes('amazonaws.com')) {
                    // First error and it's an S3 URL - try the proxy URL
                    console.log('InfoModal - S3 URL failed, trying proxy');
                    setImageError(true);
                    return;
                  }
                  
                  // Try alternative formats for ibb.co
                  if (themeImageUrl && themeImageUrl.includes('ibb.co/') && !e.target.dataset.retried) {
                    e.target.dataset.retried = 'true';
                    const match = themeImageUrl.match(/ibb\.co\/([A-Za-z0-9]+)/);
                    if (match) {
                      const imageId = match[1];
                      // Try different extensions
                      const alternatives = [
                        `https://i.ibb.co/${imageId}/image.png`,
                        `https://i.ibb.co/${imageId}/image.jpeg`,
                        `https://i.ibb.co/${imageId}/image.webp`
                      ];
                      
                      let currentIndex = 0;
                      const tryNext = () => {
                        if (currentIndex < alternatives.length) {
                          console.log('InfoModal - Trying alternative URL:', alternatives[currentIndex]);
                          setImageLoading(true); // Show skeleton again for retry
                          e.target.src = alternatives[currentIndex];
                          currentIndex++;
                        } else {
                          // All alternatives failed
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }
                      };
                      
                      e.target.onerror = tryNext;
                      tryNext();
                      return;
                    }
                  }
                  
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              
              {/* Error State */}
              <div className="hidden absolute inset-0 flex-col items-center justify-center text-gray-500 dark:text-gray-400 bg-white dark:bg-[#181818] rounded-xl">
                <div className="text-4xl mb-2">🖼️</div>
                <div className="text-sm font-medium">Image unavailable</div>
                <div className="text-xs mt-1 opacity-75">Please check the image source</div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Progress Indicator (if multiple modals) */}
        {modals.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
            <div className="flex items-center space-x-1">
              {modals.map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full ${
                    index === currentModalIndex 
                      ? 'bg-blue-500' 
                      : index < currentModalIndex 
                        ? 'bg-green-500' 
                        : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default InfoModal;